import React, { useState } from 'react';
import { MinusIcon, PlusIcon } from 'lucide-react';
import type { Product, VariantSelection } from '../../types/product';
import { ProductGallery } from './ProductGallery';
import { VariantSelector } from './VariantSelector';
import { StarRating } from './StarRating';
import { StockLabel } from './StockLabel';
import { ProductPerks } from './ProductPerks';
import { StickyBuyBar } from './StickyBuyBar';
import { FrequentlyBought } from './FrequentlyBought';
import { ProductTabs } from './ProductTabs';
import { RelatedProducts } from './RelatedProducts';
import { Breadcrumbs } from '../layout/Breadcrumbs';
import { useCart } from '../../contexts/CartContext';
import { useInView } from '../../hooks/useInView';
import { categories } from '../../data/categories';
import { formatPrice } from '../../utils/currency';
import { defaultSelection, discountPercent, resolveVariant } from '../../utils/products';
export function StandardProductView({
  product
}: {
  product: Product;
}) {
  const {
    addItem
  } = useCart();
  const [selection, setSelection] = useState<VariantSelection>(() => defaultSelection(product));
  const [qty, setQty] = useState(1);
  const {
    ref: buyRef,
    inView
  } = useInView<HTMLDivElement>('-120px 0px 0px 0px');
  const v = resolveVariant(product, selection);
  const category = categories.find(c => c.id === product.category);
  const discount = discountPercent(product);
  const cta = product.stock === 'preorder' ? 'Pre-order' : 'In winkelwagen';
  const add = () => addItem(product, selection, qty);
  return <div className="mx-auto max-w-7xl px-4 pb-24 pt-5 sm:px-6 lg:px-8" data-id="element-454" data-mp="1sp705q2jqei3">
      <Breadcrumbs items={[{
      label: 'Home',
      href: '/'
    }, {
      label: category?.name ?? 'Assortiment',
      href: `/shop/${product.category}`
    }, {
      label: product.name
    }]} data-id="element-455" data-mp="1nut21fwi1t9q" />

      <div className="mt-5 grid gap-8 lg:grid-cols-12 lg:gap-12" data-id="element-456" data-mp="2gerlgfm69nuj">
        <div className="lg:col-span-7" data-id="element-457" data-mp="2bl6kqft72frn">
          <div className="lg:sticky lg:top-40" data-id="element-458" data-mp="2bk59obc6ht9x">
            <ProductGallery images={product.gallery} alt={product.name} activeImage={v.image} badges={<>
                  {discount > 0 && <span className="rounded-md bg-sale px-2 py-1 text-xs font-bold text-white" data-id="element-460" data-mp="3w2xxnoyej5v5">−{discount}%</span>}
                  {product.badge && <span className={`rounded-md px-2 py-1 text-xs font-bold ${product.badge === 'Nieuw' ? 'bg-volt text-ink' : 'bg-ink text-white'}`} data-id="element-461" data-mp="4vd37gxhhl3i">
                      {product.badge}
                    </span>}
                </>} data-id="element-459" data-mp="9ph7fsfd7f0w" />
          </div>
        </div>

        <div className="lg:col-span-5" data-id="element-462" data-mp="3i47vvw0n0i0g">
          <p className="text-sm font-medium text-muted" data-id="element-463" data-mp="3i50xyjonijc9">{product.brand}</p>
          <h1 className="mt-1 text-2xl font-bold leading-tight tracking-tight text-ink sm:text-[32px]" data-id="element-464" data-mp="1bd8ljjp31y65">{product.name}</h1>
          <a href="#details" className="mt-3 inline-flex items-center gap-2 text-sm" data-id="element-465" data-mp="1gbjsnmnztuko">
            <StarRating rating={product.rating} data-id="element-466" data-mp="1qg0483ufwpio" />
            <span className="text-muted underline-offset-2 hover:underline" data-id="element-467" data-mp="1qep0g40zvhrk">
              {product.rating.toLocaleString('nl-NL')} · {product.reviews.toLocaleString('nl-NL')} reviews
            </span>
          </a>

          <ul className="mt-5 flex flex-wrap gap-1.5" data-id="element-468" data-mp="1uierd0556bfr">
            {product.specs.map(s => <li key={s} className="rounded-md bg-surface px-2.5 py-1.5 text-xs font-semibold text-ink" data-id="element-469" data-mp="297auqjw970a1">
                {s}
              </li>)}
          </ul>

          <div className="mt-6 flex items-baseline gap-3" data-id="element-470" data-mp="24aapgb78a1jz">
            <span className="text-[34px] font-bold leading-none tracking-tight text-ink" data-id="element-471" data-mp="2i6xxpa4mse57">{formatPrice(v.price)}</span>
            {v.compareAt && <span className="text-base text-muted line-through" data-id="element-472" data-mp="2e75johwgxfsg">{formatPrice(v.compareAt)}</span>}
          </div>
          <p className="mt-1.5 text-xs text-muted" data-id="element-473" data-mp="nvrlmnfkho1p">
            Incl. btw · of 3 × {formatPrice(Math.round(v.price / 3 * 100) / 100)} renteloos met Klarna
          </p>

          {product.variants && product.variants.length > 0 && <div className="mt-6 space-y-5 border-t border-line pt-6" data-id="element-474" data-mp="q7trreqan0lg">
              {product.variants.map(g => <VariantSelector key={g.id} group={g} value={selection[g.id]} onChange={id => setSelection(s => ({
            ...s,
            [g.id]: id
          }))} data-id="element-475" data-mp="ubi03zmr207r" />)}
            </div>}

          <div ref={buyRef} className="mt-6 flex gap-3" data-id="element-476" data-mp="2jtstritw3vs">
            <div className="flex h-12 items-center rounded-lg border border-line" data-id="element-477" data-mp="2h7mfv7obuf5">
              <button type="button" onClick={() => setQty(q => Math.max(1, q - 1))} aria-label="Aantal verlagen" className="flex h-full w-11 items-center justify-center text-ink hover:bg-surface" data-id="element-478" data-mp="6ijcu2ydgjoe">
                <MinusIcon className="h-4 w-4" data-id="element-479" data-mp="3ezwa09i9u87r" />
              </button>
              <span className="w-8 text-center text-sm font-semibold tabular-nums" aria-live="polite" data-id="element-480" data-mp="3jyqpp2kxzgnn">
                {qty}
              </span>
              <button type="button" onClick={() => setQty(q => q + 1)} aria-label="Aantal verhogen" className="flex h-full w-11 items-center justify-center text-ink hover:bg-surface" data-id="element-481" data-mp="3cdwxui3ebph3">
                <PlusIcon className="h-4 w-4" data-id="element-482" data-mp="2yf6skf6fajhn" />
              </button>
            </div>
            <button type="button" onClick={add} className="h-12 flex-1 rounded-lg bg-volt text-sm font-bold text-ink transition-[background-color,transform] duration-150 hover:bg-volt-hover active:scale-[0.98]" data-id="element-483" data-mp="2thewd8yqwb7n">
              {cta}
            </button>
          </div>
          <div className="mt-3" data-id="element-484" data-mp="2ettwrp12wu4h">
            <StockLabel status={product.stock} long data-id="element-485" data-mp="2eqefqgnuhb0f" />
          </div>

          <div className="mt-6" data-id="element-486" data-mp="2aq4c3aniywoq">
            <ProductPerks data-id="element-487" data-mp="25rkluy1weyqk" />
          </div>
        </div>
      </div>

      <div className="mt-14 space-y-12" data-id="element-488" data-mp="25scj0db594iy">
        <FrequentlyBought product={product} selection={selection} mainPrice={v.price} mainImage={v.image} data-id="element-489" data-mp="1w46vbj1rumr7" />
        <ProductTabs product={product} data-id="element-490" data-mp="2061x0onm725i" />
      </div>

      <RelatedProducts product={product} data-id="element-491" data-mp="206v8g2xiqftz" />

      <StickyBuyBar visible={!inView} image={v.image} name={product.name} options={product.variants ? v.labels : []} price={v.price} compareAt={v.compareAt} ctaLabel={cta} onAdd={add} data-id="element-492" data-mp="1m6amwz07y5ws" />
    </div>;
}